// src/pages/AuthScreen.js
//
// Combined login/signup screen. Rather than two separate routes each
// rendering a full page, this is one screen with two halves — a form
// panel and a gradient panel — that cross-slide to swap sides when you
// switch between "Sign in" and "Sign up", instead of a full page
// navigation. The route (/login or /signup) still exists and sets which
// mode you land in, and switching modes updates the URL to match without
// a hard reload.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, Phone, Sun, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { pendingCheckoutRedirect } from "../lib/pendingCheckout";
import { defaultLandingRoute } from "../lib/businessType";

export default function AuthScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, signup, loginWithGoogle, resetPassword } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const [mode, setMode] = useState(location.pathname === "/signup" ? "signup" : "login");

  // Keep mode in sync if the user navigates directly (e.g. a bookmarked
  // /signup link) rather than clicking the in-screen toggle.
  useEffect(() => {
    setMode(location.pathname === "/signup" ? "signup" : "login");
  }, [location.pathname]);

  function switchMode(next) {
    setMode(next);
    navigate(next === "signup" ? "/signup" : "/login", { replace: true });
  }

  // ── Login form state ──
  const [loginEmail, setLoginEmail]       = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError]       = useState("");
  const [loginLoading, setLoginLoading]   = useState(false);

  const [showForgot, setShowForgot]     = useState(false);
  const [forgotEmail, setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError]   = useState("");
  const [forgotSent, setForgotSent]     = useState(false);

  async function handleForgotPassword(e) {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    try {
      await resetPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch (err) {
      // Deliberately vague — confirming whether an email exists on file
      // is an account-enumeration risk, and Firebase's own error codes
      // for this (auth/user-not-found) would leak exactly that.
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      const { getDoc: getDocFn, doc: docFn } = await import("firebase/firestore");
      const { db } = await import("../lib/firebase");
      const snap = await getDocFn(docFn(db, "businesses", result.user.uid));
      const profile = snap.data();
      const pending = pendingCheckoutRedirect();
      navigate(profile?.role === "admin" ? "/admin" : pending || defaultLandingRoute());
    } catch (err) {
      setLoginError(
        err.code === "auth/invalid-credential"
          ? "Invalid email or password."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoginLoading(false);
    }
  }

  // ── Google sign-in — works for both login and signup panels, since
  // loginWithGoogle() itself creates the businesses doc on a first-time
  // sign-in (no separate "signup via Google" path needed). ──
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  async function handleGoogleAuth() {
    setGoogleError("");
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle();
      const { getDoc: getDocFn, doc: docFn } = await import("firebase/firestore");
      const { db } = await import("../lib/firebase");
      const snap = await getDocFn(docFn(db, "businesses", result.user.uid));
      const profile = snap.data();
      if (profile?.role === "admin") { navigate("/admin"); return; }
      if (!profile?.businessName) { navigate("/onboarding"); return; }
      navigate(pendingCheckoutRedirect() || defaultLandingRoute());
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") { setGoogleLoading(false); return; }
      setGoogleError("Couldn't sign in with Google. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  }

  function renderGoogleButton() {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
          <div style={{ flex: 1, height: 1, background: "var(--border-2)" }} />
          <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 600 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: "var(--border-2)" }} />
        </div>
        {googleError && (
          <div style={{ background: "var(--red-lt)", border: "1px solid rgba(239,68,68,.3)", borderRadius: "var(--radius)", padding: "11px 14px", marginBottom: 14, color: "#f87171", fontSize: 13 }}>
            {googleError}
          </div>
        )}
        <button
          type="button"
          onClick={handleGoogleAuth}
          disabled={googleLoading}
          style={{
            width: "100%", padding: "12px 24px",
            background: "var(--surface)", border: "1px solid var(--border-2)", borderRadius: "var(--radius)",
            color: "var(--text-1)", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit",
            cursor: googleLoading ? "not-allowed" : "pointer", opacity: googleLoading ? 0.6 : 1,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}
        >
          <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l6-6C34.1 5.5 29.3 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5 44.5 35.3 44.5 24c0-1.2-.1-2.4-.3-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l6-6C34.1 5.5 29.3 3.5 24 3.5c-7.7 0-14.4 4.4-17.7 10.9z"/>
            <path fill="#4CAF50" d="M24 44.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.6 2.2-7.2 2.2-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.9 16.2 44.5 24 44.5z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C40.9 36 44.5 30.5 44.5 24c0-1.2-.1-2.4-.3-3.5z"/>
          </svg>
          {googleLoading ? "Signing in…" : "Continue with Google"}
        </button>
      </>
    );
  }


  const [signupForm, setSignupForm] = useState({ email: "", phone: "", password: "", confirm: "" });
  const [signupErrors, setSignupErrors] = useState({});
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupGlobalError, setSignupGlobalError] = useState("");

  function signupField(key) {
    return {
      value: signupForm[key],
      onChange: e => setSignupForm(f => ({ ...f, [key]: e.target.value })),
    };
  }

  function validateSignup() {
    const e = {};
    if (!signupForm.email.trim())        e.email    = "Required";
    if (!signupForm.phone.trim())        e.phone    = "Required";
    if (signupForm.password.length < 6)  e.password = "Min. 6 characters";
    if (signupForm.password !== signupForm.confirm) e.confirm = "Passwords don't match";
    return e;
  }

  async function handleSignup(e) {
    e.preventDefault();
    const errs = validateSignup();
    if (Object.keys(errs).length) { setSignupErrors(errs); return; }
    setSignupLoading(true);
    setSignupGlobalError("");
    try {
      await signup(signupForm.email, signupForm.password, signupForm.phone);
      navigate("/onboarding");
    } catch (err) {
      setSignupGlobalError(
        err.code === "auth/email-already-in-use"
          ? "An account with this email already exists."
          : "Registration failed. Please try again."
      );
    } finally {
      setSignupLoading(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "12px 14px 12px 42px",
    background: "rgba(255,255,255,.05)",
    border: "1px solid var(--border-2)",
    borderRadius: "var(--radius)",
    color: "var(--text-1)", fontSize: 14, outline: "none",
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", letterSpacing: ".08em", textTransform: "uppercase", display: "block", marginBottom: 8 };

  const isSignup = mode === "signup";

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-3)", padding: 24 }}>
      <div style={{
        position: "relative", width: "100%", maxWidth: 900, height: 620,
        borderRadius: 20, overflow: "hidden", boxShadow: "0 30px 80px -20px rgba(0,0,0,.4)",
        background: "var(--surface)",
      }}>

        {/* Theme toggle — fixed to the whole screen, above both sliding panels */}
        <button
          onClick={toggleTheme}
          style={{
            position: "absolute", top: 18, right: 18, zIndex: 10,
            width: 34, height: 34, borderRadius: "50%",
            background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", cursor: "pointer",
          }}
        >
          {isDark ? <Sun size={14}/> : <Moon size={14}/>}
        </button>

        {/* Form panel — slides between the left and right half */}
        <div style={{
          position: "absolute", top: 0, left: 0, width: "50%", height: "100%",
          transform: isSignup ? "translateX(100%)" : "translateX(0)",
          transition: "transform .6s cubic-bezier(.65,0,.35,1)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "40px 44px", overflowY: "auto",
        }}>
          {!isSignup ? (
            <form onSubmit={handleLogin} style={{ width: "100%", maxWidth: 340 }}>
              <h1 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, color: "var(--text-1)", marginBottom: 6 }}>Welcome back</h1>
              <p style={{ color: "var(--text-3)", fontSize: 13.5, marginBottom: 26 }}>Sign in to manage your reviews and campaigns.</p>

              {loginError && (
                <div style={{ background: "var(--red-lt)", border: "1px solid rgba(239,68,68,.3)", borderRadius: "var(--radius)", padding: "11px 14px", marginBottom: 18, color: "#f87171", fontSize: 13 }}>
                  {loginError}
                </div>
              )}

              <label style={labelStyle}>Email address</label>
              <div style={{ position: "relative", marginBottom: 16 }}>
                <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }}/>
                <input type="email" required placeholder="you@company.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} style={inputStyle}/>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={labelStyle}>Password</label>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(loginEmail); setForgotSent(false); setForgotError(""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "var(--accent)", padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>
              <div style={{ position: "relative", marginBottom: 22 }}>
                <Lock size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }}/>
                <input type="password" required placeholder="Your password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} style={inputStyle}/>
              </div>

              <button type="submit" disabled={loginLoading} style={{
                width: "100%", padding: "13px 24px",
                background: loginLoading ? "rgba(0,113,235,.4)" : "var(--accent)",
                border: "none", borderRadius: "var(--radius)", color: "#fff",
                fontSize: 15, fontWeight: 700, fontFamily: "inherit",
                cursor: loginLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
                {loginLoading ? "Signing in…" : "Sign in"}
              </button>
              {renderGoogleButton()}
            </form>
          ) : (
            <form onSubmit={handleSignup} style={{ width: "100%", maxWidth: 340 }}>
              <h1 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, color: "var(--text-1)", marginBottom: 6 }}>Create your account</h1>
              <p style={{ color: "var(--text-3)", fontSize: 13.5, marginBottom: 22 }}>Start collecting 5-star reviews automatically.</p>

              {signupGlobalError && (
                <div style={{ background: "var(--red-lt)", border: "1px solid rgba(239,68,68,.3)", borderRadius: "var(--radius)", padding: "11px 14px", marginBottom: 16, color: "#f87171", fontSize: 13 }}>
                  {signupGlobalError}
                </div>
              )}

              {[
                { key: "email",    label: "Email address", icon: Mail,  type: "email" },
                { key: "phone",    label: "Phone number",  icon: Phone, type: "tel"   },
                { key: "password", label: "Password",      icon: Lock,  type: "password" },
                { key: "confirm",  label: "Confirm password", icon: Lock, type: "password" },
              ].map(({ key, label, icon: Icon, type }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>{label}</label>
                  <div style={{ position: "relative" }}>
                    <Icon size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }}/>
                    <input
                      type={type} {...signupField(key)}
                      style={{ ...inputStyle, borderColor: signupErrors[key] ? "var(--red)" : "var(--border-2)" }}
                    />
                  </div>
                  {signupErrors[key] && <p style={{ fontSize: 12, color: "#f87171", marginTop: 4 }}>{signupErrors[key]}</p>}
                </div>
              ))}

              <button type="submit" disabled={signupLoading} style={{
                width: "100%", padding: "13px 24px", marginTop: 6,
                background: signupLoading ? "rgba(0,113,235,.4)" : "var(--accent)",
                border: "none", borderRadius: "var(--radius)", color: "#fff",
                fontSize: 15, fontWeight: 700, fontFamily: "inherit",
                cursor: signupLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
                {signupLoading ? "Creating account…" : "Create account"}
              </button>
              {renderGoogleButton()}
            </form>
          )}
        </div>

        {/* Gradient panel — slides opposite the form panel */}
        <div style={{
          position: "absolute", top: 0, left: "50%", width: "50%", height: "100%",
          transform: isSignup ? "translateX(-100%)" : "translateX(0)",
          transition: "transform .6s cubic-bezier(.65,0,.35,1)",
          background: "linear-gradient(135deg, var(--accent) 0%, #0047ab 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 48, textAlign: "center", overflow: "hidden",
        }}>
          {/* Decorative circles, purely visual */}
          <div style={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,.08)", top: -80, right: -80 }}/>
          <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)", bottom: -60, left: -60 }}/>

          <div style={{ position: "relative", maxWidth: 300 }}>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "#fff", marginBottom: 14 }}>
              {isSignup ? "Already have an account?" : "New to TechBugz?"}
            </h2>
            <p style={{ color: "rgba(255,255,255,.85)", fontSize: 14, lineHeight: 1.6, marginBottom: 26 }}>
              {isSignup
                ? "Sign in to get back to your dashboard, campaigns, and reviews."
                : "Create an account and let Zilo AI start managing your reputation today."}
            </p>
            <button
              onClick={() => switchMode(isSignup ? "login" : "signup")}
              style={{
                padding: "11px 30px", borderRadius: "var(--radius)",
                background: "transparent", border: "1.5px solid rgba(255,255,255,.7)",
                color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              {isSignup ? "Sign in" : "Sign up"}
            </button>
          </div>
        </div>

      </div>

      {showForgot && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,.6)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 32, width: "100%", maxWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
            {forgotSent ? (
              <>
                <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Check your inbox</h2>
                <p style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 22 }}>
                  If an account exists for <strong>{forgotEmail}</strong>, a password reset link is on its way.
                </p>
                <button
                  onClick={() => setShowForgot(false)}
                  style={{ width: "100%", padding: "12px 24px", borderRadius: "var(--radius)", border: "none", background: "var(--accent)", color: "#fff", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Done
                </button>
              </>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Reset your password</h2>
                <p style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 20 }}>Enter your email and we'll send you a link to set a new one.</p>

                {forgotError && (
                  <div style={{ background: "var(--red-lt)", border: "1px solid rgba(239,68,68,.3)", borderRadius: "var(--radius)", padding: "11px 14px", marginBottom: 14, color: "#f87171", fontSize: 13 }}>
                    {forgotError}
                  </div>
                )}

                <label style={labelStyle}>Email address</label>
                <div style={{ position: "relative", marginBottom: 20 }}>
                  <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }}/>
                  <input type="email" required placeholder="you@company.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} style={inputStyle}/>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button" onClick={() => setShowForgot(false)}
                    style={{ flex: 1, padding: "12px 24px", borderRadius: "var(--radius)", border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-1)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit" disabled={forgotLoading}
                    style={{ flex: 1, padding: "12px 24px", borderRadius: "var(--radius)", border: "none", background: forgotLoading ? "rgba(0,113,235,.4)" : "var(--accent)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: forgotLoading ? "not-allowed" : "pointer", fontFamily: "inherit" }}
                  >
                    {forgotLoading ? "Sending…" : "Send link"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
